import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FileSignature, Building2 } from "lucide-react";

// Garante que aceites comerciais pendentes sejam a PRIMEIRA coisa tratada.
// Montador: redireciona para Valores das Empresas (1x por sessão).
// Montadora: overlay bloqueante até aceitar ou recusar.
export default function AceitesGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendentes, setPendentes] = useState([]);
  const lastFetch = React.useRef(0);
  const prevPath = React.useRef("");

  const rota = user?.role === "montadora" ? "/montadora/condicoes-empresas" : "/montador/condicoes";
  const rotaValores = user?.role === "montadora" ? "/montadora/valores-empresas" : "/montador/valores-empresas";

  useEffect(() => {
    if (!user || !["montador", "montadora"].includes(user.role)) return;
    const decisao = (p) => p.includes("condicoes") || p.includes("valores-empresas");
    const saiuDeDecisao = decisao(prevPath.current) && !decisao(location.pathname);
    prevPath.current = location.pathname;
    // Evita refetch (com backfill/WhatsApp no backend) a cada navegação: 1x/min, ou ao sair da tela de decisão
    if (!saiuDeDecisao && Date.now() - lastFetch.current < 60000) return;
    lastFetch.current = Date.now();
    const ep = user.role === "montadora" ? "/montadora/condicoes" : "/montador/condicoes";
    api.get(ep).then(r => {
      const pend = (r.data || []).filter(a => a.status === "pendente");
      setPendentes(pend);
      if (user.role === "montador" && pend.length > 0 &&
          sessionStorage.getItem("mj_aceites_redirect") !== "1" &&
          !location.pathname.includes("valores-empresas") && !location.pathname.includes("condicoes")) {
        sessionStorage.setItem("mj_aceites_redirect", "1");
        toast.info(`Você tem ${pend.length} contrato(s) de empresa aguardando seu aceite.`, { duration: 7000 });
        navigate(rotaValores);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user, location.pathname]);

  const recusar = async (a) => {
    if (!window.confirm(`Recusar as condições de ${a.empresa_nome || "esta empresa"}?`)) return;
    try {
      await api.post(`/condicoes-aceite/${a.id}/responder`, { acao: "recusar" });
      setPendentes(prev => prev.filter(x => x.id !== a.id));
      toast.success("Condições recusadas.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Overlay bloqueante para MONTADOR e MONTADORA fora das telas de decisão
  if (!user || !["montador", "montadora"].includes(user.role) || pendentes.length === 0) return null;
  if (location.pathname.includes("condicoes") || location.pathname.includes("valores-empresas")) return null;

  return (
    <div className="fixed inset-0 z-[9000] bg-[#0A174A]/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="aceites-gate-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center gap-2 text-aj-blue-deep font-display font-extrabold text-lg">
          <FileSignature className="text-aj-orange" size={20} /> Novas empresas na sua região
        </div>
        <p className="text-sm text-aj-text-muted mt-1">
          Para continuar usando o painel, aceite ou recuse as condições comerciais abaixo. O aceite é digital e auditado.
        </p>
        <div className="mt-4 space-y-3 max-h-[45vh] overflow-auto">
          {pendentes.map(a => (
            <div key={a.id} className="border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3"
              data-testid={`gate-aceite-${a.id}`}>
              <Building2 size={16} className="text-aj-orange shrink-0" />
              <div className="flex-1 text-sm font-bold text-aj-blue-deep">{a.empresa_nome || "Empresa"}</div>
              <button onClick={() => navigate(user.role === "montador" ? rotaValores : rota)}
                className="px-3 py-1.5 rounded-md bg-[#FF6700] hover:bg-[#e05a00] text-white text-xs font-bold"
                data-testid={`gate-aceitar-${a.id}`}>
                Analisar e aceitar
              </button>
              <button onClick={() => recusar(a)}
                className="px-3 py-1.5 rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-red-50 text-xs font-bold"
                data-testid={`gate-recusar-${a.id}`}>
                Recusar
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
